//ApplauseForMe.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const ApplauseForMe = () => {
  const location = useLocation();
  const authState = useSelector(state => state.authReducer);
  const [applauses, setApplauses] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedApplauseId, setSelectedApplauseId] = useState(null);
  const [reply, setReply] = useState('');
  const [postedReply, setPostedReply] = useState(false);
  const [postedReplyMsg, setPostedReplyMsg] = useState('');
  const [postReplyClicked, setPostReplyClicked] = useState(false);
  const [applauseRoute, setApplauseRoute] = useState("i-applauded");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const myRoute = (location?.pathname || '').split('/').pop() || '';
    setApplauseRoute(myRoute);
  }, [location.pathname]);

  const fetchApplauses = useCallback(async () => {
    const config = {
      url: "/applause/forme",
      method: "post",
      headers: { Authorization: authState.token },
      data: { 
        userId: authState.user._id, 
        nameCode: authState.user.think5whyscode 
      }
    };

    try {
      const data = await fetchData(config, { showSuccessToast: false });
      setApplauses(data.applauses);
    } catch (error) {
      console.error('Error fetching applauses:', error);
    }
  }, [
    applauseRoute, 
    authState.token, 
    authState.user._id, 
    authState.user.think5whyscode, 
    fetchData
  ]);

  useEffect(() => {
    fetchApplauses();
  }, [fetchApplauses]);

  const handleView = (applauseId) => {
    setSelectedApplauseId(prevId => prevId === applauseId ? null : applauseId);
  };

  const handlePostReply = (applauseId) => {
    setPostReplyClicked(true);

    if (reply.trim() === '') {
      setErrors({ reply: "Reply must not be blank" });
      return;
    }

    postReply(applauseId, reply);
  };

  const postReply = useCallback((applauseId, reply) => {
    const date = new Date();
    const config = {
      url: "/applause/postreply",
      method: "post",
      headers: { Authorization: authState.token },
      data: { applauseId, reply, replyDate: date }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response && response.status) {
          setPostedReply(true);
          setPostedReplyMsg("Reply Posted");
        } else {
          setPostedReplyMsg("Error Posting Reply. Please try again.");
        }
      })
      .catch(error => {
        console.error("Error posting reply:", error);
      });
  }, [authState.token, fetchData]);

  const formatDateTime = (date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const sortedApplauses = [...applauses].sort((a, b) => new Date(b.applauseDate) - new Date(a.applauseDate));

  return (
    <div className='bg-black'>
      <div className='border-b border-blue-400'>
        <h3 className='text-lg lg:text-xl border-b border-blue-400 text-yellow-200 mt-1 py-2'>👏👏 Others Applauded Me</h3>
      </div>
      <div className="min-h-[80vh] border border-gray-300 p-4 mt-4">
        <div className="mt-1">
          {applauses.length !== 0 && (
            <div className="flex justify-between items-center border-b border-b-gray-300 mb-1">
              <h3 className='text-m mt-1 mx-2 text-white'>{applauses.length} Applause For You Found!</h3>

              <Link to='/applause/add' className='block w-24 mb-2 h-8 px-3 py-1 bg-blue-500 rounded-md mr-2'>
                <FontAwesomeIcon className="mr-2" icon={faPlus} /> Add New
              </Link>
            </div>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div>
            {applauses.length === 0 ? (
              <div className='h-[300px] flex items-center justify-center text-yellow-300'>
                <h3 className='text-xl text-orange-400'>
                  Currently there is no Applause for you!
                </h3>
              </div>
            ) : (
              sortedApplauses.map((applause, index) => (
                <div key={applause._id} className='bg-black border borde-gray-700 my-4 p-4 text-white rounded-md shadow-md'>
                  <div className='flex'>
                    <span className='font-small'>
                      #{index + 1} By {applause.name} as {applause.applauseTo} On {formatDateTime(applause.applauseDate)}
                    </span>

                    <Tooltip text={"View details"} position={"top"}>
                      <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(applause._id)}>
                        <i className="fa-solid fa-eye"></i>
                      </span>
                    </Tooltip>
                  </div>

                  {selectedApplauseId === applause._id && (
                    <div className='bg-black text-white p-2 mt-2'>
                      <p><strong>You Applauded By:</strong> {applause.userName} <strong>As:</strong> {applause.applauseTo}</p>
                      <p><strong>Applauded For:</strong> {applause.applauseFor}</p>
                      <p><strong>Message:</strong> {applause.description}</p>
                      <p><strong>Date:</strong> {formatDateTime(applause.applauseDate)}</p>

                      {applause.reply && (
                        <>
                          <p className='text-purple-700'><strong>Reply:</strong> {applause.reply}</p>
                          <p className='text-purple-700'><strong>Reply Date:</strong> {formatDateTime(applause.replyDate)}</p>
                        </>
                      )}
                    </div>
                  )}

                  {selectedApplauseId === applause._id && !applause.reply && (
                    <div className="flex flex-col bg-black text-white p-2 mt-2">
                      <textarea
                        className="bg-slate-100 p-2 w-full"
                        style={{ height: '150px' }}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Input your reply..."
                      />
                      {errors.reply && <p className='text-red-600'>{errors.reply}</p>}
                      {postReplyClicked && <p className='text-yellow-600'>{postedReplyMsg}</p>}

                      <button
                        className='bg-purple-700 text-white w-20 px-4 py-2 mt-2 rounded'
                        onClick={() => handlePostReply(applause._id)}
                        disabled={postedReply}
                      >
                        Post
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <footer className="bg-gray-900 border-t border-blue-400 text-gray-400 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApplauseForMe;
